import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MapComponent from '../MapComponent';
import BuildingParametersPanel from './BuildingParametersPanel';
import { mapiService } from '../../services/mapiService';
import { building3DService } from '../../services/building3DService';
import './Building3DGenerator.css';
import FindButtonImg from '../../assets/Find-button.png';

const Building3DGenerator = () => {
  const [coordinates, setCoordinates] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [buildingParams, setBuildingParams] = useState(null);
  const [showParametersPanel, setShowParametersPanel] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  
  // Search states
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Building click handler
  const handleBuildingClick = useCallback(async (buildingFeature, coordinates) => {
    console.log('🏢 Building clicked:', buildingFeature, coordinates);
    
    try {
      setGenerationStatus('analyzing');
      
      // Получаем параметры здания из фичи
      const properties = buildingFeature.properties || {};
      
      // Создаем объект параметров здания
      const params = {
        // Координаты
        coordinates: coordinates,
        center: [coordinates.lng, coordinates.lat],
        
        // Основные параметры из OSM
        building_type: properties.building || 'unknown',
        name: properties.name || '',
        height: properties.height ? parseFloat(properties.height) : null,
        levels: properties.levels ? parseInt(properties.levels) : null,
        min_height: properties.min_height ? parseFloat(properties.min_height) : 0,
        
        // Адрес
        address: {
          housenumber: properties.addr_housenumber || properties['addr:housenumber'] || '',
          street: properties.addr_street || properties['addr:street'] || '',
          city: properties.addr_city || properties['addr:city'] || '',
          postcode: properties.addr_postcode || properties['addr:postcode'] || ''
        },
        
        // Геометрия (если есть)
        geometry: buildingFeature.geometry,
        
        // Площадь основания (примерная, если есть геометрия)
        base_area: building3DService.calculateBuildingArea(buildingFeature.geometry),
        
        // OSM данные
        osm_id: properties.osm_id || properties.id,
        source: 'osm',
        
        // Дополнительные параметры
        roof_shape: properties.roof_shape || properties['roof:shape'] || 'flat',
        roof_material: properties.roof_material || properties['roof:material'] || 'unknown',
        wall_material: properties.wall_material || properties['wall:material'] || 'unknown',
        
        // Статус заполнения
        completion_status: {
          has_geometry: !!buildingFeature.geometry,
          has_height: !!(properties.height || properties.levels),
          has_address: !!(properties.addr_housenumber || properties.name),
          has_building_type: !!properties.building
        }
      };
      
      // Пытаемся получить дополнительную информацию через API
      try {
        const additionalData = await building3DService.getBuildingDetails(
          coordinates.lat, 
          coordinates.lng
        );
        
        if (additionalData) {
          Object.assign(params, additionalData);
        }
      } catch (error) {
        console.warn('Could not fetch additional building data:', error);
      }
      
      setSelectedBuilding(buildingFeature);
      setBuildingParams(params);
      setShowParametersPanel(true);
      setGenerationStatus('ready');
      
    } catch (error) {
      console.error('Error analyzing building:', error);
      setGenerationStatus('error');
    }
  }, []);

  // Search functions (copied from MapDemo)
  const search = useCallback(async (searchText) => {
    if (searchText.length < 3) {
      setResults([]);
      setLoading(false);
      return;
    }
    
    setResults([]);
    setLoading(true);
    
    try {
      const data = await mapiService.searchPlaces(searchText);
      setResults(data);
    } catch (error) {
      console.error('Error searching places:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (query) {
        search(query);
      } else {
        setResults([]);
      }
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [query, search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
        if (!query) {
          setIsSearchActive(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [query]);

  const getPlaceDetails = (place) => {
    const region = place.region;
    const country = place.country;
    const type = place.type || 'Place';

    if (!region || 
        region.toLowerCase() === 'unknown' || 
        region === 'Unknown' ||
        region.trim() === '') {
      return `${type} of ${country}`;
    }

    if (region === country) {
      return country;
    }

    return `${region}, ${country}`;
  };

  const shouldShowType = (place) => {
    const region = place.region;
    return region && 
           region.toLowerCase() !== 'unknown' && 
           region !== 'Unknown' && 
           region.trim() !== '';
  };

  const handlePlaceholderClick = () => {
    setIsSearchActive(true);
    setShowDropdown(true);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleSelectPlace = (place) => {
    setQuery(place.name);
    setResults([]);
    setShowDropdown(false);
    setIsSearchActive(false);
    setSelectedPlace(place);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setShowDropdown(true);
    
    if (e.target.value === '') {
      setResults([]);
    }
  };

  const handleSearchButtonClick = () => {
    if (isSearchActive && query) {
      if (results.length > 0) {
        handleSelectPlace(results[0]);
      } else {
        search(query);
      }
    } else {
      handlePlaceholderClick();
    }
  };

  // Обработка генерации 3D модели
  const handleGenerate3D = async (finalParams) => {
    try {
      setGenerationStatus('generating');
      
      const result = await building3DService.generate3DModel(finalParams);
      
      if (result.success) {
        setGenerationStatus('completed');
        // Здесь можно показать результат или скачать файл
        console.log('3D Model generated successfully:', result);
      } else {
        setGenerationStatus('error');
      }
      
    } catch (error) {
      console.error('3D Generation error:', error);
      setGenerationStatus('error');
    }
  };

  return (
    <div className="building3d-generator-container">
      {/* Header */}
      <div className="building3d-header">
        <div className="building3d-header-content">
          <div className="building3d-header-flex">
            <div className="building3d-header-left">
              <Link to="/" className="back-link">
                ← Back to Home
              </Link>
              <div className="building3d-logo-text">
                🏗️ 3D Building Generator
              </div>
            </div>
            <div className="building3d-header-right">
              <div className="generation-status">
                <div className={`status-dot ${generationStatus || 'idle'}`}></div>
                <span>
                  {generationStatus === 'analyzing' && 'Analyzing Building...'}
                  {generationStatus === 'ready' && 'Ready to Generate'}
                  {generationStatus === 'generating' && 'Generating 3D Model...'}
                  {generationStatus === 'completed' && 'Model Generated!'}
                  {generationStatus === 'error' && 'Error Occurred'}
                  {!generationStatus && 'Click on a Building'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="building3d-main-content">
        
        {/* Map Section */}
        <div className="building3d-map-section">
          <div className="building3d-map-container">
            <MapComponent
              onCoordinatesChange={setCoordinates}
              selectedPlace={selectedPlace}
              onBuildingClick={handleBuildingClick}
              showH3Grid={false}
              enableBuildingClick={true}
            />
            
            {/* Search Overlay */}
            <div className="building3d-search-overlay">
              <div className="building3d-search-container">
                {!isSearchActive ? (
                  <div 
                    className="building3d-search-placeholder"
                    onClick={handlePlaceholderClick}
                  >
                    Search for a location...
                  </div>
                ) : (
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={handleInputChange}
                    placeholder="Search for a location..."
                    className="building3d-search-input"
                    onFocus={() => setShowDropdown(true)}
                  />
                )}
                
                {loading && <div className="building3d-search-spinner"></div>}
                
                <button 
                  className="building3d-search-button"
                  onClick={handleSearchButtonClick}
                  style={{
                    backgroundImage: `url(${FindButtonImg})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center'
                  }}
                >
                  <span className="building3d-search-button-text">Find place</span>
                </button>
              </div>
              
              {/* Search Dropdown */}
              {showDropdown && results.length > 0 && (
                <ul ref={dropdownRef} className="building3d-search-dropdown">
                  {results.map((place, index) => (
                    <li 
                      key={place.id} 
                      onClick={() => handleSelectPlace(place)} 
                      className={`building3d-search-dropdown-item ${index === 0 ? 'first-result' : ''}`}
                    >
                      <div className="building3d-dropdown-content">
                        <div className="building3d-dropdown-place-name">{place.name}</div>
                        <span className="building3d-dropdown-separator">•</span>
                        <div className="building3d-dropdown-place-details">
                          {getPlaceDetails(place)}
                        </div>
                      </div>
                      {shouldShowType(place) && (
                        <div className="building3d-dropdown-place-type">
                          {place.type || 'Place'}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Instructions Overlay */}
            {!selectedBuilding && (
              <div className="building3d-instructions">
                <div className="instructions-content">
                  <h3>🏗️ Generate 3D Building Models</h3>
                  <p>Click on any building on the map to start generating a 3D model</p>
                  <ul>
                    <li>🎯 Click directly on a building</li>
                    <li>📋 Review and complete building parameters</li>
                    <li>📸 Upload photos for texture generation</li>
                    <li>🏗️ Generate your 3D model</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Building Parameters Panel */}
        {showParametersPanel && buildingParams && (
          <BuildingParametersPanel
            buildingParams={buildingParams}
            onClose={() => {
              setShowParametersPanel(false);
              setSelectedBuilding(null);
              setBuildingParams(null);
            }}
            onGenerate3D={handleGenerate3D}
            generationStatus={generationStatus}
          />
        )}

      </div>
    </div>
  );
};

export default Building3DGenerator;